// @systeme: navigation-client
// ============================================================================
// PairsForm — Redirections héritées : LA table des anciennes URL (lot 9.0)
// ============================================================================
// Une URL qui a existé ne meurt jamais : elle vit dans des emails déjà envoyés,
// des signets, des `lienHref` de notifications PERSISTÉS en base, des QR codes.
// Avant le lot 9, quarante redirections étaient écrites à la main dans cinq
// fichiers de routes, sous quatre formes (`<Navigate>` nu, `RedirectAvecQuery`,
// trois composants à paramètres, un par cas). Ici : UNE table, UN composant
// (`RedirectionHeritee`, routes/communs.tsx), UN garde-fou
// (`scripts/check-urls-heritees.mts`) qui vérifie que chaque ancienne URL du
// contrat (`routes/urls-heritees.json`) est encore servie ou redirigée, que
// chaque cible existe, qu'aucune redirection n'en appelle une autre et que le
// code lui-même ne lie plus jamais une URL héritée.
//
// Ce module est PUR (aucun import, aucun JSX) : il est chargé tel quel par
// Node dans le garde-fou et par Vitest — la même fonction `cibleRedirection`
// sert à l'exécution dans le navigateur et à la vérification en CI.
//
// Grammaire de `de` : un motif React Router (`:param`, suffixe `/*`).
// Grammaire de `vers` : le chemin canonique, où `:param` reprend la valeur
// capturée, `*` le reste du chemin et `{slug}` le segment de l'org du jeton
// (niveau organisation, lot 9.1 : la redirection attend la session, un visiteur
// non connecté passe par /login?returnTo=…) ; une query propre (`?tab=…`) est
// autorisée. La query de l'URL d'origine est TOUJOURS conservée (les
// paramètres propres à la cible priment en cas de conflit) — c'est ce que
// faisait `RedirectAvecQuery`, généralisé : `?tab=documents` circule dans des
// emails déjà envoyés.
//
// Lot 9.1 : les redirections des quatre niveaux d'URL (`/o/:slug`…) viendront
// s'ajouter ICI, jamais dans un fichier de routes.
// ============================================================================

export interface RedirectionHeritee {
  /** Ancienne URL — motif React Router (`:param`, suffixe `/*` autorisé). */
  de: string;
  /** Chemin canonique ; `:param` substitué, `*` = reste du chemin, query propre autorisée. */
  vers: string;
  /** Pourquoi l'ancienne URL a existé et depuis quand elle redirige (documentation vivante). */
  motif: string;
}

/** Ce que la résolution d'une redirection sait de la session (lot 9.1). */
interface ContexteRedirection {
  /** Segment d'URL de l'org du jeton (slug, à défaut id) pour les cibles `{slug}`. */
  slug?: string | null;
}

/** Contexte d'exemple partagé par le garde-fou et les tests. */
export const CONTEXTE_EXEMPLE: ContexteRedirection = { slug: "ex-slug" };

/** Une cible du niveau organisation : la redirection a besoin de l'org du jeton. */
export function cibleDependDeLOrg(entree: RedirectionHeritee): boolean {
  return entree.vers.includes("{slug}");
}

const REDIRECTIONS_LITTERALES: readonly RedirectionHeritee[] = [
  // --- Catalogue public et pages plateforme Inter ---------------------------
  {
    de: "/plateforme",
    vers: "/catalogue/codev",
    motif: "Ancienne racine de la marketplace ; le catalogue Codev a son URL propre (chantier « 2 catalogues »).",
  },
  {
    de: "/plateforme/:id",
    vers: "/plateforme/formation/:id",
    motif: "Dette N20 résorbée (9.1.D) : la page produit publique canonique est /plateforme/formation/:id.",
  },
  {
    de: "/plateforme/propositions",
    vers: "/o/{slug}/programmation",
    motif: "Propositions dépréciées : repliées dans le cockpit Pipeline (valider / refuser).",
  },
  {
    de: "/plateforme/mes-groupes",
    vers: "/mon-espace/animation/groupes",
    motif: "Bloc 7.B : liste des groupes animés unifiée dans le hub animateur (Inter + Intra) ; PlateformeMesGroupesPage supprimée le 2026-08-28.",
  },
  {
    de: "/plateforme/groupes/:id",
    vers: "/groupes/:id",
    motif: "Décision D (Phase 3, 2026-08-06) : le détail d'un groupe Inter vit sous l'espace animateur ; des lienHref de notifications persistés portent encore l'ancienne forme.",
  },
  {
    de: "/plateforme/groupes/:groupeId/seances/:seanceId",
    vers: "/groupes/:groupeId/seances/:seanceId",
    motif: "Même décision D pour la page séance d'un groupe Inter.",
  },
  {
    de: "/plateforme/mes-lettres-mission",
    vers: "/mon-espace/animation/dossier?tab=contractuel",
    motif: "Consolidation 2026-06-25 : « Mon dossier animateur » à onglets ; l'ancienne page autonome devient l'onglet contractuel (alias → documents).",
  },
  {
    de: "/plateforme/mes-documents-animateur",
    vers: "/mon-espace/animation/dossier?tab=pieces",
    motif: "Consolidation 2026-06-25 : l'ancienne page des pièces devient l'onglet pièces (alias → documents).",
  },
  {
    de: "/plateforme/mon-statut",
    vers: "/mon-espace/animation/dossier?tab=statut",
    motif: "Consolidation 2026-06-25 : « Ma situation » devient l'onglet statut du dossier animateur.",
  },
  {
    de: "/mon-parcours",
    vers: "/mon-espace",
    motif: "Bloc 7.C : accueil participant unifié — /mon-parcours (accueil Inter dupliqué) rejoint l'accueil canonique.",
  },
  {
    de: "/mes-documents-inter",
    vers: "/mon-espace/inscriptions?tab=documents",
    motif: "Bloc 7.C : documents du participant unifiés (Inter + Intra).",
  },
  {
    de: "/mes-seances/emails",
    vers: "/mon-espace/emails",
    motif: "Bloc 7.C (B6) : l'historique des emails du participant a sa page propre.",
  },

  // --- Pilotage -------------------------------------------------------------
  {
    de: "/dashboard",
    vers: "/o/{slug}/projets",
    motif: "Ancien tableau de bord pilote ; la liste des projets est l'entrée du pilotage (DashboardPage supprimée au lot 7.0).",
  },
  {
    de: "/projets/calendrier",
    vers: "/o/{slug}/projets",
    motif: "Calendrier des projets retiré ; l'agenda vit dans les pages de groupe et l'accueil personnel.",
  },
  {
    de: "/admin/reporting-financier",
    vers: "/o/{slug}/reporting-financier",
    motif: "Doctrine URL (lot résorption 2026-08-06) : les pages org-scopées quittent le préfixe gouvernance /admin/* pour /organisation/* ; query conservée.",
  },
  {
    de: "/admin/qualite",
    vers: "/o/{slug}/qualite",
    motif: "Doctrine URL 2026-08-06 : page org-scopée sous /organisation/* ; ?tab= des digests Qualiopi conservé.",
  },
  {
    de: "/admin/satisfaction",
    vers: "/o/{slug}/satisfaction",
    motif: "Doctrine URL 2026-08-06 : page org-scopée sous /organisation/*.",
  },
  {
    de: "/admin/plateforme/templates",
    vers: "/o/{slug}/bibliotheque?tab=documents",
    motif: "Réconciliation modèles Inter (2026-08-07) : l'éditeur legacy v1 est retiré, les modèles s'éditent sur la page générique de la famille 4 dans le contexte d'org choisi au sélecteur.",
  },
  {
    de: "/admin/plateforme/animateurs",
    vers: "/o/{slug}/animateurs",
    motif: "Placeholder Admin Inter supprimé (décision G, lot résorption 2026-08-06) : l'annuaire réel des animateurs.",
  },
  {
    de: "/admin/plateforme/reglages/questionnaires",
    vers: "/o/{slug}/bibliotheque?tab=questionnaires",
    motif: "Placeholder Admin Inter supprimé (2026-08-06) : la page questionnaires générique de la bibliothèque.",
  },
  {
    de: "/admin/plateforme/reglages/mesure-impact",
    vers: "/o/{slug}/projets/mesure-impact",
    motif: "Placeholder Admin Inter supprimé (2026-08-06) : le suivi d'impact.",
  },

  // --- Espace personnel -----------------------------------------------------
  {
    de: "/profil",
    vers: "/mon-espace/profil",
    motif: "L'ancien /profil est l'espace personnel unifié (Mon profil).",
  },
  {
    de: "/espace-perso/outils",
    vers: "/mon-espace/outils",
    motif: "Ancien hub « Outils & IA » remplacé par « Mes outils Codev » (réorganisation FX 2026-08-23).",
  },
  {
    de: "/ma-formation",
    vers: "/mon-espace",
    motif: "Ancienne entrée participant ; l'accueil personnel est /mes-seances.",
  },
  {
    de: "/animation",
    vers: "/mon-espace",
    motif: "Ancienne entrée animateur ; l'accueil personnel porte aussi le bandeau animateur.",
  },

  // --- Espace formateur (= animateur) et espace participant historiques ------
  {
    de: "/espace-formateur",
    vers: "/mon-espace/profil",
    motif: "Fusion espace-formateur → espace personnel ; direct vers la cible finale (l'ancien saut via /profil faisait une double redirection, N198).",
  },
  {
    de: "/espace-formateur/profil",
    vers: "/mon-espace/profil",
    motif: "Fusion espace-formateur → profil unifié.",
  },
  {
    de: "/espace-formateur/documents",
    vers: "/mon-espace/animation/dossier?tab=statut",
    motif: "Doublon fermé (FX 2026-08-19) : la route servait la matrice du STAFF au rôle ANIMATEUR ; l'animateur a « Ma situation » et « Mes documents » dans son dossier.",
  },
  {
    de: "/espace-formateur/lettres-mission",
    vers: "/mon-espace/animation/dossier?tab=contractuel",
    motif: "Redirigeait par erreur vers la page PILOTE /organisation/lettres-mission (« Accès non autorisé ») ; renvoie vers le coffre animateur.",
  },
  {
    de: "/espace-formateur/facturation",
    vers: "/plateforme/remuneration",
    motif: "Fusion espace-formateur finie (lot résorption 2026-08-06) : la facturation animateur ne vit qu'à l'URL canonique admin ; query conservée.",
  },
  {
    de: "/espace-formateur/revenus",
    vers: "/mon-espace/animation/revenus",
    motif: "Bloc 7.B : revenus animateur dans le hub.",
  },
  {
    de: "/espace-participant",
    vers: "/mon-espace/profil",
    motif: "Fusion espace-participant → espace personnel ; direct vers la cible finale (N198).",
  },
  {
    de: "/espace-participant/profil",
    vers: "/mon-espace/profil",
    motif: "Fusion espace-participant → profil unifié.",
  },
  {
    de: "/espace-participant/documents",
    vers: "/mon-espace/inscriptions?tab=documents",
    motif: "Documents participant unifiés (bloc 7.C).",
  },
  {
    de: "/espace-participant/questionnaires",
    vers: "/mon-espace/questionnaires",
    motif: "Les questionnaires du participant vivent sous l'accueil personnel.",
  },
  {
    de: "/mes-seances/documents",
    vers: "/mon-espace/inscriptions?tab=documents",
    motif: "Bloc 7.C : les documents Intra rejoignent la page unifiée.",
  },

  // --- Correcteur RS 7137 (anciennes URL racine, avec leur id — N198) --------
  {
    de: "/correcteur",
    vers: "/mon-espace/outils/correcteur",
    motif: "Le correcteur est un outil de l'espace personnel (pod isolé) ; emails de session et signets portent encore la racine.",
  },
  {
    de: "/correcteur/sessions/:id",
    vers: "/mon-espace/outils/correcteur/sessions/:id",
    motif: "Lien profond d'une session de jury : l'id est conservé (rediriger vers la racine le perdait, N198).",
  },
  {
    de: "/correcteur/epreuve/:candidatureId",
    vers: "/mon-espace/outils/correcteur/epreuve/:candidatureId",
    motif: "Lien profond de l'épreuve d'un candidat, id conservé (N198).",
  },
  {
    de: "/correcteur/correction/:candidatureId",
    vers: "/mon-espace/outils/correcteur/correction/:candidatureId",
    motif: "Lien profond de la correction d'une candidature, id conservé (N198).",
  },

  // --- Lot 9.1 : quatre niveaux d'URL — anciennes adresses des niveaux personnel et plateforme ---
  {
    de: "/crm/affaires",
    vers: "/plateforme/crm/affaires",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/crm/comptes",
    vers: "/plateforme/crm/comptes",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/organisations",
    vers: "/plateforme/organisations",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/tarifs",
    vers: "/plateforme/tarifs",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/factures-clients",
    vers: "/plateforme/factures-clients",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/relances-debits",
    vers: "/plateforme/relances-debits",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/pool-animateurs",
    vers: "/o/{slug}/programmation?tab=axes",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/pipeline",
    vers: "/o/{slug}/programmation",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/marges",
    vers: "/plateforme/marges",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/absences",
    vers: "/plateforme/absences",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/dashboard",
    vers: "/plateforme/dashboard",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/candidatures",
    vers: "/o/{slug}/animateurs?tab=candidatures",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/annuaire-certifications",
    vers: "/o/{slug}/animateurs?tab=certifications",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/beneficiaires",
    vers: "/plateforme/beneficiaires",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/paiements",
    vers: "/plateforme/paiements",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/journal-envois",
    vers: "/plateforme/journal-envois",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/opcos",
    vers: "/plateforme/opcos",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/supervision",
    vers: "/plateforme/supervision",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/projets-intra",
    vers: "/o/{slug}/projets?tab=tous",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/referent-ia",
    vers: "/plateforme/referent-ia",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/outils-ia",
    vers: "/plateforme/outils-ia",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/support",
    vers: "/plateforme/support",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/support-technique",
    vers: "/plateforme/support-technique",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/demandes-peda-commerciales",
    vers: "/plateforme/demandes-peda-commerciales",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/espace-perso",
    vers: "/mon-espace/profil",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils-codev",
    vers: "/mon-espace/outils",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/karaoke",
    vers: "/mon-espace/outils/karaoke",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/transcription",
    vers: "/mon-espace/outils/transcription",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/transcripteur-recherche",
    vers: "/mon-espace/outils/transcripteur-recherche",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/transcripteur-recherche/live/:seanceId",
    vers: "/mon-espace/outils/transcripteur-recherche/live/:seanceId",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur",
    vers: "/mon-espace/outils/correcteur",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur/sessions/:id",
    vers: "/mon-espace/outils/correcteur/sessions/:id",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur/epreuve/:candidatureId",
    vers: "/mon-espace/outils/correcteur/epreuve/:candidatureId",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur/correction/:candidatureId",
    vers: "/mon-espace/outils/correcteur/correction/:candidatureId",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur/cas-pratiques/:id",
    vers: "/mon-espace/outils/correcteur/cas-pratiques/:id",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/outils/correcteur/sessions/:id/jury",
    vers: "/mon-espace/outils/correcteur/sessions/:id/jury",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/e-learning",
    vers: "/mon-espace/e-learning",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/e-learning/contrat-ouvert",
    vers: "/mon-espace/e-learning/contrat-ouvert",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/e-learning/clarification-etape-2",
    vers: "/mon-espace/e-learning/clarification-etape-2",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/e-learning/apports-etape-4",
    vers: "/mon-espace/e-learning/apports-etape-4",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-perso/e-learning/manuel/:cle",
    vers: "/mon-espace/e-learning/manuel/:cle",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-seances",
    vers: "/mon-espace",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/entrainement",
    vers: "/mon-espace/entrainement",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/entrainement/:id",
    vers: "/mon-espace/entrainement/:id",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-groupes/:groupeId",
    vers: "/mon-espace/groupes/:groupeId",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/admin/animateur-documents/validation",
    vers: "/o/{slug}/animateurs?tab=validation",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/conformite-documents",
    vers: "/o/{slug}/animateurs?tab=conformite",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/admin/plateforme/remuneration",
    vers: "/plateforme/remuneration",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau plateforme `/plateforme`.",
  },
  {
    de: "/mes-seances/questionnaires",
    vers: "/mon-espace/questionnaires",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-seances/communication",
    vers: "/mon-espace/echanges",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/plateforme/proposer",
    vers: "/mon-espace/animation/propositions",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`. Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements.",
  },
  {
    de: "/plateforme/mon-espace",
    vers: "/mon-espace/inscriptions",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`. Lot 10.5 : le suivi Inter est fondu dans la liste unique de « Mes inscriptions » (`?tab=inter` reste un alias).",
  },
  {
    de: "/plateforme/inscriptions-supervisees",
    vers: "/mon-espace/inscriptions-supervisees",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-groupes",
    vers: "/mon-espace/inscriptions",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-documents",
    vers: "/mon-espace/inscriptions?tab=documents",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/mes-emails",
    vers: "/mon-espace/emails",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/plateforme/devenir-animateur",
    vers: "/mon-espace/devenir-animateur",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur",
    vers: "/mon-espace/animation",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/groupes",
    vers: "/mon-espace/animation/groupes",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/contacts-a-valider",
    vers: "/mon-espace/animation/contacts-a-valider",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/candidatures",
    vers: "/mon-espace/animation/propositions?tab=positionnements",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`. Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements.",
  },
  {
    de: "/espace-animateur/proposer-evenement",
    vers: "/mon-espace/animation/propositions?tab=evenement",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`. Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements.",
  },
  {
    de: "/mon-espace/animation/proposer",
    vers: "/mon-espace/animation/propositions",
    motif: "Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements. Le formulaire (créneau attribué `?candidature=`, porte privée `?prive=1`) est l'onglet par défaut.",
  },
  {
    de: "/mon-espace/animation/positionnements",
    vers: "/mon-espace/animation/propositions?tab=positionnements",
    motif: "Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements.",
  },
  {
    de: "/mon-espace/animation/proposer-evenement",
    vers: "/mon-espace/animation/propositions?tab=evenement",
    motif: "Lot 10.7 (2026-10-18) : « Mes propositions » à onglets (N235) — proposer un groupe, proposer un événement, mes positionnements.",
  },
  {
    de: "/espace-animateur/administration",
    vers: "/mon-espace/animation/dossier",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/mes-comptes",
    vers: "/mon-espace/animation/comptes-clients",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/entretien-annuel",
    vers: "/mon-espace/animation/entretien-annuel",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/documents",
    vers: "/mon-espace/animation/documents",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },
  {
    de: "/espace-animateur/revenus",
    vers: "/mon-espace/animation/revenus",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau personnel `/mon-espace`.",
  },

  // --- Lot 9.1 : quatre niveaux d'URL — anciennes adresses du niveau organisation (/o/:slug) ---
  {
    de: "/organisation/qualite",
    vers: "/o/{slug}/qualite",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/satisfaction",
    vers: "/o/{slug}/satisfaction",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/formation",
    vers: "/o/{slug}/formation",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets",
    vers: "/o/{slug}/projets",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/nouveau",
    vers: "/o/{slug}/projets/nouveau",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:id/edit",
    vers: "/o/{slug}/projets/:id/edit",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:projetId/impact/tableau-de-bord",
    vers: "/o/{slug}/projets/:projetId/impact/tableau-de-bord",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:projetId/produits/:produitId/*",
    vers: "/o/{slug}/projets/:projetId/produits/:produitId/*",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:id/*",
    vers: "/o/{slug}/projets/:id/*",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:projetId/groupes/nouveau",
    vers: "/o/{slug}/projets/:projetId/groupes/nouveau",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:projetId/groupes/:groupeId/edit",
    vers: "/o/{slug}/projets/:projetId/groupes/:groupeId/edit",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  // (/projets/:projetId[/produits/:produitId]/groupes/:groupeId/* : la page
  //  groupe Intra, lot 9.2c — voir REDIRECTIONS_PAGE_GROUPE_INTRA plus bas.)
  {
    de: "/projets/:projetId/groupes/:groupeId/generer-seances",
    vers: "/o/{slug}/projets/:projetId/groupes/:groupeId/generer-seances",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/:projetId/produits/:produitId/groupes/:groupeId/seances/:seanceId",
    vers: "/o/{slug}/groupes/:groupeId/seances/:seanceId",
    motif: "Lot 9.3 (2026-10-18) : LA page Séance n'a qu'une adresse, /o/:slug/groupes/:g/seances/:s, Intra comme Inter (modèle §8.2) ; l'ancienne hiérarchie projet › produit › groupe redirige.",
  },
  {
    de: "/projets/:projetId/groupes/:groupeId/seances/:seanceId",
    vers: "/o/{slug}/groupes/:groupeId/seances/:seanceId",
    motif: "Lot 9.3 (2026-10-18) : LA page Séance n'a qu'une adresse, /o/:slug/groupes/:g/seances/:s, Intra comme Inter (modèle §8.2) ; l'ancienne hiérarchie projet › groupe redirige.",
  },
  {
    de: "/o/:slug/projets/:projetId/produits/:produitId/groupes/:groupeId/seances/:seanceId",
    vers: "/o/:slug/groupes/:groupeId/seances/:seanceId",
    motif: "Lot 9.3 (2026-10-18) : LA page Séance n'a qu'une adresse — la forme hiérarchique posée au 9.1 (projet › produit › groupe › séance) redirige, slug conservé.",
  },
  {
    de: "/o/:slug/projets/:projetId/groupes/:groupeId/seances/:seanceId",
    vers: "/o/:slug/groupes/:groupeId/seances/:seanceId",
    motif: "Lot 9.3 (2026-10-18) : LA page Séance n'a qu'une adresse — la forme hiérarchique posée au 9.1 (projet › groupe › séance) redirige, slug conservé.",
  },
  {
    de: "/espace-animateur/groupes/:groupeId/seances/:seanceId",
    vers: "/groupes/:groupeId/seances/:seanceId",
    motif: "Lot 9.1 (2026-10-18) : la page d'un groupe Inter rejoint le niveau organisation (/o/:slug/groupes/:id) — par la porte /groupes/:id, qui retrouve l'org du groupe quel que soit le jeton.",
  },
  {
    de: "/animateurs",
    vers: "/o/{slug}/animateurs",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/participants",
    vers: "/o/{slug}/participants",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/parametres",
    vers: "/o/{slug}/parametres",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/cockpit-reglages",
    vers: "/o/{slug}/cockpit-reglages",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/equipe",
    vers: "/o/{slug}/equipe",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bpf",
    vers: "/o/{slug}/bpf",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/catalogue",
    vers: "/o/{slug}/bibliotheque",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/tarifs",
    vers: "/o/{slug}/bibliotheque?tab=tarifs",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/modeles-emails",
    vers: "/o/{slug}/bibliotheque?tab=emails",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/modeles-documents",
    vers: "/o/{slug}/bibliotheque?tab=documents",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/questionnaires",
    vers: "/o/{slug}/bibliotheque?tab=questionnaires",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/mesure-impact",
    vers: "/o/{slug}/projets/mesure-impact",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/impact/resultats/:campagneId",
    vers: "/o/{slug}/impact/resultats/:campagneId",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/questionnaires-impact",
    vers: "/o/{slug}/bibliotheque?tab=protocoles",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/prompts-bilan",
    vers: "/o/{slug}/bibliotheque?tab=prompts",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/bibliotheque/referentiels-codev",
    vers: "/o/{slug}/bibliotheque?tab=referentiels",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/automatisations",
    vers: "/o/{slug}/automatisations",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/lettres-mission",
    vers: "/o/{slug}/animateurs?tab=lettres-mission",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/projets/exports",
    vers: "/o/{slug}/projets/exports",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/disponibilites",
    vers: "/o/{slug}/animateurs?tab=disponibilites",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/espace-animateur/groupes/:id",
    vers: "/groupes/:id",
    motif: "Lot 9.1 (2026-10-18) : la page d'un groupe Inter rejoint le niveau organisation (/o/:slug/groupes/:id) — par la porte /groupes/:id, qui retrouve l'org du groupe quel que soit le jeton.",
  },
  {
    de: "/mon-espace/inscriptions-inter",
    vers: "/mon-espace/inscriptions",
    motif: "Lot 9.3b (2026-10-18) : le suivi Inter (réservations, paiements, rétractation) devient un onglet de « Mes inscriptions » (modèle §8.2) ; adresse posée au 9.1, page supprimée. Lot 10.5 : l'onglet disparaît à son tour, les volets Inter vivent sur la carte de l'inscription (`?tab=inter` reste un alias).",
  },
  {
    de: "/mon-espace/documents",
    vers: "/mon-espace/inscriptions?tab=documents",
    motif: "Lot 9.3b (2026-10-18) : les documents du participant sont l'onglet « Documents » de « Mes inscriptions » (modèle §8.2) ; adresse posée au 9.1, page supprimée.",
  },
  {
    de: "/mes-actions",
    vers: "/o/{slug}",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
  {
    de: "/organisation/reporting-financier",
    vers: "/o/{slug}/reporting-financier",
    motif: "Lot 9.1 (2026-10-18) : quatre niveaux d'URL — l'adresse rejoint le niveau organisation `/o/:slug` (le slug de l'org du jeton).",
  },
];

// ---------------------------------------------------------------------------
// Lot 9.2c — LA page groupe (/o/:slug/groupes/:id, modèle §8.0) : l'ancienne
// page Intra habitait le projet (/projets/:p[/produits/:pp]/groupes/:g) et
// portait ses onglets en SEGMENTS d'URL. Chaque segment devient un `?tab=`
// (une entrée par segment : la grammaire de `vers` ne traduit pas un suffixe
// libre), le suffixe libre retombe sur la page. Quatre bases : les deux
// formes plates du lot 9.1 ({slug} = org du jeton) et les deux formes
// /o/:slug (le slug capturé). Les pages annexes (edit, generer-seances,
// seances/:seanceId) restent servies : plus statiques, elles gagnent.
// ---------------------------------------------------------------------------
const MOTIF_PAGE_GROUPE_INTRA =
  "Lot 9.2c (2026-10-18) : LA page groupe /o/:slug/groupes/:id, Intra comme Inter — les onglets de l'ancienne page Intra (segments d'URL) deviennent ?tab=.";

/** Segment de l'ancienne page Intra → onglet de la page unique (null = la page). */
const SEGMENTS_PAGE_GROUPE_INTRA: ReadonlyArray<readonly [segment: string, tab: string | null]> = [
  ["suivi", null],
  ["seances", "seances"],
  ["equipe", "equipe"],
  ["communication", "fil"],
  ["questionnaires", "questionnaires"],
  ["impact", "questionnaires"],
  ["config-questionnaires", "config-questionnaires"],
  ["documents", "documents"],
  ["bilan", "bilan"],
  ["certification", "certification"],
  ["emargements", "emargements"],
  ["sondages", "sondages"],
];

const BASES_PAGE_GROUPE_INTRA: ReadonlyArray<readonly [de: string, vers: string]> = [
  ["/projets/:projetId/groupes/:groupeId", "/o/{slug}/groupes/:groupeId"],
  ["/projets/:projetId/produits/:produitId/groupes/:groupeId", "/o/{slug}/groupes/:groupeId"],
  ["/o/:slug/projets/:projetId/groupes/:groupeId", "/o/:slug/groupes/:groupeId"],
  ["/o/:slug/projets/:projetId/produits/:produitId/groupes/:groupeId", "/o/:slug/groupes/:groupeId"],
];

const REDIRECTIONS_PAGE_GROUPE_INTRA: readonly RedirectionHeritee[] = BASES_PAGE_GROUPE_INTRA.flatMap(([de, vers]) => [
  { de: `${de}/*`, vers, motif: MOTIF_PAGE_GROUPE_INTRA },
  ...SEGMENTS_PAGE_GROUPE_INTRA.map(([segment, tab]) => ({
    de: `${de}/${segment}`,
    vers: tab ? `${vers}?tab=${tab}` : vers,
    motif: MOTIF_PAGE_GROUPE_INTRA,
  })),
]);

// ---------------------------------------------------------------------------
// Lot 9.5 — pages à onglets (MENU_CIBLE §4, décisions 1, 3, 4 et 6 du
// 2026-10-14) : une page devenue onglet GARDE son adresse, en redirection vers
// `?tab=` — Animateurs (/o/:slug/animateurs), Bibliothèque
// (/o/:slug/bibliotheque), Projets (/o/:slug/projets, « les miens / tous »),
// Programmation (/o/:slug/programmation : le module de l'org marketplace,
// modèle §8.3). Les adresses `/plateforme/*` des vues plateforme rejoignent
// l'org du jeton ({slug}) ; les adresses `/o/:slug/*` gardent le slug capturé.
// Les entrées plus anciennes qui visaient ces pages (lot 9.1 et avant) ont été
// re-ciblées ici même : jamais de chaîne.
// ---------------------------------------------------------------------------
const MOTIF_PAGES_A_ONGLETS =
  "Lot 9.5 (2026-10-18) : la page est un onglet d'une page à onglets (MENU_CIBLE §4) — son adresse redirige vers ?tab=.";

const PAGES_A_ONGLETS: ReadonlyArray<readonly [de: string, vers: string]> = [
  ["/o/:slug/bibliotheque/catalogue", "/o/:slug/bibliotheque"],
  ["/o/:slug/bibliotheque/tarifs", "/o/:slug/bibliotheque?tab=tarifs"],
  ["/o/:slug/bibliotheque/modeles-emails", "/o/:slug/bibliotheque?tab=emails"],
  ["/o/:slug/bibliotheque/modeles-documents", "/o/:slug/bibliotheque?tab=documents"],
  ["/o/:slug/bibliotheque/questionnaires", "/o/:slug/bibliotheque?tab=questionnaires"],
  ["/o/:slug/bibliotheque/questionnaires-impact", "/o/:slug/bibliotheque?tab=protocoles"],
  ["/o/:slug/bibliotheque/prompts-bilan", "/o/:slug/bibliotheque?tab=prompts"],
  ["/o/:slug/bibliotheque/referentiels-codev", "/o/:slug/bibliotheque?tab=referentiels"],
  ["/o/:slug/lettres-mission", "/o/:slug/animateurs?tab=lettres-mission"],
  ["/o/:slug/disponibilites", "/o/:slug/animateurs?tab=disponibilites"],
  ["/plateforme/candidatures", "/o/{slug}/animateurs?tab=candidatures"],
  ["/plateforme/conformite-documents", "/o/{slug}/animateurs?tab=conformite"],
  ["/plateforme/animateur-documents/validation", "/o/{slug}/animateurs?tab=validation"],
  ["/plateforme/annuaire-certifications", "/o/{slug}/animateurs?tab=certifications"],
  ["/plateforme/projets-intra", "/o/{slug}/projets?tab=tous"],
  ["/plateforme/pipeline", "/o/{slug}/programmation"],
  ["/plateforme/pool-animateurs", "/o/{slug}/programmation?tab=axes"],
];

const REDIRECTIONS_PAGES_A_ONGLETS: readonly RedirectionHeritee[] = PAGES_A_ONGLETS.map(([de, vers]) => ({
  de,
  vers,
  motif: MOTIF_PAGES_A_ONGLETS,
}));

export const REDIRECTIONS_HERITEES: readonly RedirectionHeritee[] = [
  ...REDIRECTIONS_LITTERALES,
  ...REDIRECTIONS_PAGE_GROUPE_INTRA,
  ...REDIRECTIONS_PAGES_A_ONGLETS,
];

/** Noms des paramètres (`:nom`) d'un motif, dans l'ordre ; `*` pour le suffixe libre. */
export function parametresDuMotif(motif: string): string[] {
  const noms = Array.from(motif.matchAll(/:([A-Za-z_][A-Za-z0-9_]*)/g), (m) => m[1]);
  if (motif.endsWith("/*")) noms.push("*");
  return noms;
}

/** Substitue `:param` et `*` dans un motif ou une cible à partir des paramètres capturés. */
function substituer(chemin: string, params: Readonly<Record<string, string | undefined>>): string {
  let resultat = chemin.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_tout, nom: string) => params[nom] ?? "");
  if (resultat.endsWith("/*")) {
    const reste = params["*"];
    resultat = resultat.slice(0, -2) + (reste ? `/${reste}` : "");
  }
  return resultat;
}

/** Fusionne la query propre à la cible et celle de l'URL d'origine (la cible prime). */
function fusionnerQueries(propre: string, origine: string): string {
  const fusion = new URLSearchParams(origine.startsWith("?") ? origine.slice(1) : origine);
  new URLSearchParams(propre).forEach((valeur, cle) => fusion.set(cle, valeur));
  return fusion.toString();
}

/**
 * L'URL canonique où mène une ancienne URL : paramètres capturés substitués
 * dans `vers`, `{slug}` remplacé par l'org du jeton, query d'origine conservée
 * (celle de `vers` prime).
 */
export function cibleRedirection(
  entree: RedirectionHeritee,
  params: Readonly<Record<string, string | undefined>>,
  search: string,
  contexte: ContexteRedirection = {},
): string {
  const [cheminVers, queryVers = ""] = entree.vers.split("?");
  const chemin = substituer(cheminVers.replace("{slug}", contexte.slug ?? "-"), params);
  const query = fusionnerQueries(queryVers, search);
  return query ? `${chemin}?${query}` : chemin;
}

/** Paramètres d'exemple pour un motif (`:id` → `ex-id`, `*` → `a/b`) — garde-fou et tests. */
export function parametresExemple(motif: string): Record<string, string> {
  const params: Record<string, string> = {};
  for (const nom of parametresDuMotif(motif)) params[nom] = nom === "*" ? "a/b" : `ex-${nom}`;
  return params;
}

/** Une URL concrète qui matche le motif (paramètres d'exemple substitués). */
export function cheminExemple(motif: string): string {
  return substituer(motif, parametresExemple(motif));
}
